import React, { useEffect, useState, useRef } from 'react';
interface CursorState {
  position: {
    x: number;
    y: number;
  };
  isVisible: boolean;
  variant: 'default' | 'enter' | 'leave' | 'menuFocusIn' | 'menuMouseDown' | 'menuClick' | 'mouse-down-to-default' | 'cursorFocusTitle' | 'selectProject';
  text?: string;
  scale?: number;
}
export function CustomCursor() {
  const [cursor, setCursor] = useState<CursorState>({
    position: {
      x: -100,
      y: -100
    },
    isVisible: false,
    variant: 'default',
    scale: 1
  });
  const [isMobile, setIsMobile] = useState(false);
  const cursorRef = useRef<HTMLDivElement>(null);
  const circleRef = useRef<HTMLDivElement>(null);
  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth < 768;
      setIsMobile(isTouchDevice || isSmallScreen);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  // Collision detection helper
  const checkButtonCollision = (x: number, y: number): boolean => {
    // Only check collision when in selectProject mode
    if (cursor.variant !== 'selectProject') return false;
    const buttons = document.querySelectorAll('[data-cursor-shrink-target="true"]');
    const circleRadius = 50; // 100px diameter / 2
    for (const button of buttons) {
      const rect = button.getBoundingClientRect();
      // Find closest point on button to cursor
      const closestX = Math.max(rect.left, Math.min(x, rect.right));
      const closestY = Math.max(rect.top, Math.min(y, rect.bottom));
      // Calculate distance from cursor to closest point
      const distanceX = x - closestX;
      const distanceY = y - closestY;
      const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);
      // Check if circle edge overlaps with button
      if (distance < circleRadius) {
        return true;
      }
    }
    return false;
  };
  useEffect(() => {
    if (isMobile) {
      document.documentElement.classList.remove('custom-cursor');
      return;
    }
    const onMouseMove = (e: MouseEvent) => {
      const shouldShrink = checkButtonCollision(e.clientX, e.clientY);
      setCursor(prev => ({
        ...prev,
        position: {
          x: e.clientX,
          y: e.clientY
        },
        isVisible: true,
        variant: prev.variant === 'leave' ? 'enter' : prev.variant,
        scale: shouldShrink ? 0.3 : 1
      }));
    };
    const onMouseLeave = () => {
      setCursor(prev => ({
        ...prev,
        isVisible: false,
        variant: 'leave',
        scale: 1
      }));
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseleave', onMouseLeave);
    document.documentElement.classList.add('custom-cursor');
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseleave', onMouseLeave);
      document.documentElement.classList.remove('custom-cursor');
    };
  }, [isMobile, cursor.variant]);
  // Expose cursor state methods to window for other components to use
  useEffect(() => {
    if (!isMobile) {
      window.cursorDefault = () => {
        setCursor(prev => ({
          ...prev,
          variant: 'default',
          text: undefined,
          scale: 1
        }));
      };
      window.cursorMenuFocusIn = () => {
        setCursor(prev => ({
          ...prev,
          variant: 'menuFocusIn',
          text: undefined,
          scale: 1
        }));
      };
      window.cursorMenuMouseDown = () => {
        setCursor(prev => ({
          ...prev,
          variant: 'menuMouseDown',
          text: undefined,
          scale: 1
        }));
      };
      window.cursorMenuClick = () => {
        setCursor(prev => ({
          ...prev,
          variant: 'menuClick',
          text: undefined,
          scale: 1
        }));
      };
      window.cursorFocusTitle = () => {
        setCursor(prev => ({
          ...prev,
          variant: 'cursorFocusTitle',
          text: undefined,
          scale: 1
        }));
      };
      window.cursorSelectProject = () => {
        setCursor(prev => ({
          ...prev,
          variant: 'selectProject',
          text: 'select',
          scale: 1
        }));
      };
    } else {
      window.cursorDefault = () => {};
      window.cursorMenuFocusIn = () => {};
      window.cursorMenuMouseDown = () => {};
      window.cursorMenuClick = () => {};
      window.cursorFocusTitle = () => {};
      window.cursorSelectProject = () => {};
    }
  }, [isMobile]);
  if (isMobile) return null;
  return <div id="cursor" ref={cursorRef} className="custom-cursor-element" style={{
    left: `${cursor.position.x}px`,
    top: `${cursor.position.y}px`,
    zIndex: 9999999,
    position: 'fixed',
    pointerEvents: 'none',
    mixBlendMode: cursor.variant === 'selectProject' ? 'normal' : 'exclusion'
  }} data-id="element-221">
      <div id="circle1" ref={circleRef} className={cursor.variant} style={{
      transform: `scale(${cursor.scale})`,
      transition: 'transform 0.15s ease-out'
    }} data-id="element-222">
        {cursor.text}
      </div>
    </div>;
}
// Extend the Window interface to include our custom cursor methods
declare global {
  interface Window {
    cursorDefault: () => void;
    cursorMenuFocusIn: () => void;
    cursorMenuMouseDown: () => void;
    cursorMenuClick: () => void;
    cursorFocusTitle: () => void;
    cursorSelectProject: () => void;
  }
}